<template>
  <div id="supplementEdit">
    <div class="container">

      <PageTitle :title="supplement && $to(supplement.name) ? $to(supplement.name) : $t('item_type.supplement')">
        <button class="save btn btn-primary" @click="save"
          v-if="$store.getters.loggedUserCanModifyInventory">{{ $t("save") }}</button>
      </PageTitle>

      <Tabs>
        <TabSection :title="$t('items.information')" v-if="supplement">
          <template v-if="supplement">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <LocalizedInput :title="$t('items.name')" :translate="supplement.name" />
                </div>
                <div class="group" v-if="supplement.kitchen && supplement.kitchen.description">
                  <LocalizedInput :max-size="20" :title="$t('items.kitchen_description')"
                    :translate="supplement.kitchen.description" />
                </div>
                <div class="group">
                  <TipsExternalId/>
                  <label>{{ $t('items.external_id') }}</label>
                  <ExternalIDInput v-if="inventory" v-model="supplement.externalId" :inventory="inventory.id"
                                   :sync-item="supplement" input-class="editable-externalId"/>
                </div>
                <div class="group">
                  <label>{{ $t('items.taxable') }}</label>
                  <select v-model="supplement.taxable">
                    <option :value="true">{{ $t('yes') }}</option>
                    <option :value="false">{{ $t('no') }}</option>
                  </select>
                </div>
                <div class="group">
                  <input id="chkQuantifiable" type="checkbox" v-model="supplement.quantifiable">
                  <label for="chkQuantifiable">{{ $t('items.quantifiable') }}</label>
                </div>
              </div>

              <div class="column">
                <div class="group" v-for="(priceScheme, priceSchemeIndex) in supplement.priceSchemes"
                  :key="priceSchemeIndex">
                  <label>{{ $t(`scheme_methods.${priceScheme.method}`) }} ({{ currencySymbol }})</label>
                  <input type="number" v-model="priceScheme.unitPrice" step="0.01"
                    :class="{ invalid: $validate.error('supplement.priceSchemes[' + priceSchemeIndex + '].unitPrice') }">
                </div>
              </div>
            </div>
          </template>
        </TabSection>

        <TabSection :title="$t('modifiers.choices')" v-if="supplement">
          <template v-if="supplement">
            <p>{{ $t('modifiers.choices_description') }}</p>
            <ListMultiSelect :bank="choices" :bankTitle="$t('items.choice_bank')" :selected="supplement.formattedChoices"
              :selectedTitle="$t('items.selected_choices')" />
          </template>
        </TabSection>
      </Tabs>

      <div class="footer-actions">
        <button class="save btn btn-primary" @click="save"
          v-if="$store.getters.loggedUserCanModifyInventory">{{ $t("save") }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import LocalizedInput from "@/components/tools/LocalizedInput.vue";
  import ListMultiSelect from '@/components/tools/ListMultiSelect.vue';
  import { mapActions } from 'vuex';
  import PageTitle from "@/components/PageTitle.vue";
  import ExternalIDInput from "@/components/inventory/ExternalIDInput.vue";
  import TipsExternalId from "@/components/tips/TipsExternalId.vue";
  import {assignIdsOfMultiSelect, initPropertyOfMultiSelect} from "@/vueUtils";

  export default {

    name: "supplement",

    components: {LocalizedInput, ListMultiSelect, PageTitle, ExternalIDInput, TipsExternalId },

    data() {
      return {
        inventory: null,
        supplement: null,
        choices: [],
        supplementId: null,
        currencySymbol: '$'
      };
    },

    computed: {
      version() {
        return this.$store.state.contextVersion;
      },
      versionId() {
        return this.version ? this.version.id : null
      },
      inventoryReference() {
        return this.$store.state.contextCompany ? this.$store.state.contextCompany.nameCanonical : this.inventory.id;
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "inventory.supplements";
      this.$store.state.pageIcon = "/icon/menu.svg";
      this.$store.commit("showSpinner", true);

      showSpinner();
      this.supplementId = this.$route.query.supplement;
      this.inventory = await this.$store.getters.getContextInventory;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.choices = await this.getChoices({inventoryId: this.inventoryReference, augment: 'short'});

      if (this.supplementId) {
        await this.loadSupplement();
      } else {
        let supplement = {
          name: { fr: "", en: "" },
          choices: [],
          taxable: true,
          quantifiable: true,
          kitchen: {},
          priceSchemes: this.inventory.deliveryMethods.map(method => ({ method: method, unitPrice: null })),
        }
        this.fixItemForForm(supplement);
        this.supplement = supplement;
      }
      hideSpinner();

      if (this.$store.getters.isBranchContextInventory) {
        window.lockFields(this.$store.getters.getOverloadableFields);
      } else if (!this.$store.getters.loggedUserCanModifyInventory) {
        window.lockFields();
      }
    },

    validate: {
      "supplement.priceSchemes": {
        array: true,
        elements: {
          unitPrice: {
            required: true,
            maxDecimals: 2
          }
        }
      }
    },

    watch: {
      version: async function () {
        if (this.supplementId) {
          showSpinner();
          await this.loadSupplement();
          hideSpinner();
        }
      },
      "$store.getters.isBranchContextInventory"(newValue) {
        if (newValue) {
          window.lockFields(this.$store.getters.getOverloadableFields);
        }
      },
    },

    methods: {
      ...mapActions(["getSupplement", "createSupplement", "saveSupplement", "getChoices"]),

      async loadSupplement() {
        let version = this.versionId
        let supplement = await this.getSupplement({ inventoryId: this.inventoryReference, id: this.supplementId, version });
        if (!supplement) {
          console.log("erreur de fetch item");
          toast(this.$t("error.no_server"), "error");
          return;
        }
        this.fixItemForForm(supplement);
        this.supplement = supplement;
      },

      fixItemForForm(item) {
        item.kitchen = item.kitchen || {};
        item.kitchen.description = item.kitchen.description || { fr: "", en : "" };
        initPropertyOfMultiSelect(item, 'choices', 'formattedChoices', this.choices);
      },

      async save() {
        showSpinner();
        let version = this.versionId || undefined;
        let data = this.getSerializedData();
        let response = this.supplementId
            ? await this.saveSupplement({ data, version, inventoryId: this.inventoryReference, supplementId: this.supplementId })
            : await this.createSupplement({ data, version, inventoryId: this.inventoryReference });
        hideSpinner();

        if (!response) {
          toast(this.$t("error.no_server"), "error");
          return;
        }
        this.$router.push(this.$router.buildContext("/supplements"));
      },

      getSerializedData() {
        let data = JSON.parse(JSON.stringify(this.supplement));
        assignIdsOfMultiSelect(data, 'choices', 'formattedChoices');
        return data;
      }
    }
  }
</script>

<style lang="scss" scoped>
  #supplementEdit {
    display: flex;
    flex-direction: column;
  }
</style>